import React, { useRef } from "react"
import { useNavigate } from "react-router-dom"
import { reportShowPath } from "@/QuarterlyReportApp"
import { ArrowLeft } from "lucide-react"
import { ReportSection } from "~/api/communications"
import { Button } from "@/ui/Button"
import { Editor, EditorHandle } from "@/editor/Editor"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import PageHeader from "./PageHeader"
import PageFooter from "./PageFooter"
import { useCommunicationContext } from "./CommunicationContext"
import { useQuarterlyReportContext } from "./useQuarterlyReportContext"
import { usePdfView } from "./usePdfView"

const QpcspIntroLetter: React.FC = () => {
  const { communicationId, communication } = useCommunicationContext()
  const { reportId, report, isLoading, error, mock } =
    useQuarterlyReportContext()
  const navigate = useNavigate()
  const editorRef = useRef<EditorHandle>(null)

  // Intro letter content lives at the template level (QuarterlyReport), not per-household.
  // Read it directly from the communication context to avoid stale/missing section_content data.
  const editorContent = communication?.intro_letter_editorjs_content

  const { showPdfView, pdfLoading, pdfUrl, togglePdfView, loadPdfView } =
    usePdfView({
      reportId,
      sectionName: ReportSection.QPCSP_INTRO_LETTER,
      editorContent,
      mock,
    })

  const formatQuarterTitle = () => {
    if (communication?.year && communication?.quarter) {
      return `QPCSP ${communication.year} Q${communication.quarter}`
    }
    return "QPCSP Intro Letter"
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading QPCSP Intro Letter...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load report: {error.message}
      </div>
    )
  }

  if (!report) {
    return (
      <div className="alert alert-warning" role="alert">
        Report not found
      </div>
    )
  }

  return (
    <QuarterlyReportLayout
      title="QPCSP Intro Letter"
      subtitle={`${report.household_name}`}
      showEditorActions={true}
      reportId={reportId}
      showPdfView={showPdfView}
      pdfLoading={pdfLoading}
      pdfUrl={pdfUrl}
      onTogglePdfView={togglePdfView}
      actions={
        <Button
          size="md"
          variant="outline"
          icon={<ArrowLeft size={18} />}
          onClick={() =>
            navigate(reportShowPath({ communicationId, reportId }))
          }
        >
          Back to Report
        </Button>
      }
      breadcrumbProps={{
        householdName: report.household_name,
        householdPath: reportShowPath({ communicationId, reportId }),
        sectionName: ReportSection.QPCSP_INTRO_LETTER,
      }}
    >
      <div className="pdf-editor-container">
        <div className="pdf-dimensions-helper">
          Letter size (8.5 inches) -{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              loadPdfView()
            }}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            PDF View
          </a>
        </div>
        <div className="pdf-document">
          <PageHeader />
          <div className="pdf-content intro-letter-content">
            <h1 className="intro-letter-title">{formatQuarterTitle()}</h1>
            <Editor
              ref={editorRef}
              initialData={editorContent}
              onChange={() => {}}
              reportId={reportId}
              readOnly={true}
            />
          </div>
          <PageFooter />
        </div>
      </div>
    </QuarterlyReportLayout>
  )
}

export default QpcspIntroLetter
